import { Suspense, lazy, useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { Clock } from 'react-feather'
import useLiveDelivery from './useLiveDelivery'
import useSomDaEntrega from './useSomDaEntrega'
import { useAgora, useRumo } from './hooks'
import { mostraMapa, mostraMapaParado, PONTO_DE_ESPERA, haQuanto, sinalFraco, distanciaTexto, distanciaMetros, destinoEhEmpresa, nomeParaCliente, AVISO_GPS } from './entrega'
import { SeOMapaFalhar } from './mapa/AvisoSemMapa'

// O mapa da entrega ao vivo na página de rastreio. Era a página do dashboard
// num quadro (iframe); agora o betracking desenha o próprio mapa, lendo a
// entrega pela chave do acompanhamento (Murilo, 20/10/2026: "lá seria o
// lugar mesmo"). As peças do mapa são as mesmas do card do pedido no
// dashboard (copiadas em ./mapa): mudou lá, muda aqui.
const MapaAoVivo = lazy(() => import('./mapa/MapaAoVivo'))

// Sem card por cima do mapa, a folga é igual nos quatro lados. Parado: mais
// folga em cima, onde ficam o aviso de espera e a caixinha (que é alta).
const FOLGA_CENTRO = { top: 70, bottom: 70, left: 50, right: 50 }
const FOLGA_PARADO = { top: 200, bottom: 70, left: 60, right: 60 }
// Com o cartão de quem embute por cima, embaixo (o card do pedido).
const FOLGA_PARADO_COM_CARTAO = { top: 190, bottom: 120, left: 80, right: 80 }
const ALTURA = 'clamp(260px, 52vw, 420px)'

// O único mapa da entrega ao vivo do sistema. Usado aqui na página de
// rastreio e, por /mapa/:token (MapaEmbutido), por quem embute: o card do
// pedido e os Envios individuais do dashboard.
//  - `comLinha`: a linha de distância e hora embaixo (o dashboard tem o
//    próprio cartão de ETA, então desliga);
//  - `folgaParaCartao`: reserva espaço embaixo pro cartão que fica por cima;
//  - `onRota` / `onView`: quem embute fica sabendo da rota (o "chega em X
//    min") e da entrega.
/**
 * @param {{ token: string, apiUrl: string, altura?: string, comLinha?: boolean, folgaParaCartao?: boolean,
 *   onRota?: (rota: { metros: number, segundos: number } | null) => void, onView?: (view: any) => void,
 *   logos?: string[], comSom?: boolean, semTelaCheia?: boolean }} props
 */
export default function MapaDaEntrega({ token, apiUrl, altura = ALTURA, comLinha = true, folgaParaCartao = false, onRota, onView, logos = [], comSom = true, semTelaCheia = false }) {
  const base = `${apiUrl}/api/live-deliveries/share/${encodeURIComponent(token)}`
  const buscar = useCallback(() => axios.get(base, { timeout: 15000 }).then((r) => r.data), [base])
  const fonteDaRota = useCallback(() => axios.get(`${base}/route`, { timeout: 15000 }).then((r) => r.data?.rota ?? null), [base])
  const { view, inexistente, falhando } = useLiveDelivery(buscar)
  const agora = useAgora()
  const heading = useRumo(view?.position)
  const [rota, setRota] = useState(null)
  // Sem som quando quem embute já toca o dele (o card do pedido).
  useSomDaEntrega(comSom ? view?.status : null, nomeParaCliente(view?.sourceLabel))
  // Outra entrega (chave nova no mapa embutido): a rota da anterior sai.
  useEffect(() => {
    setRota(null)
  }, [token])
  useEffect(() => {
    onRota?.(rota)
  }, [rota, onRota])
  useEffect(() => {
    if (view) onView?.(view)
  }, [view, onView])

  // Link vencido ou entrega sumiu: o rastreio continua, só sem o mapa.
  if (inexistente) return null
  if (!view) return <div style={{ height: altura }} className="animate-pulse bg-[#F3EEE9]" aria-label="Carregando o mapa" />

  const comMapa = mostraMapa(view) && view.destination
  const parado = !comMapa && mostraMapaParado(view)
  if (!comMapa && !parado) return null
  const tipoDestino = destinoEhEmpresa(view) ? 'empresa' : 'casa'

  return (
    <div>
      <SeOMapaFalhar aviso={null}>
        <Suspense fallback={<div style={{ height: altura }} className="bg-[#F3EEE9]" />}>
          {comMapa ? (
            <MapaAoVivo
              key="mapa-ao-vivo"
              position={view.position}
              heading={heading}
              destination={view.destination}
              altura={altura}
              {...(folgaParaCartao ? {} : { folga: FOLGA_CENTRO })}
              logosDestino={logos}
              semTelaCheia={semTelaCheia}
              tipoDestino={tipoDestino}
              onRota={setRota}
              fonteDaRota={fonteDaRota}
            />
          ) : (
            // `key` própria: quando a entrega começa, nasce o mapa ao vivo do
            // zero, sem herdar o enquadramento da espera.
            <MapaAoVivo key="mapa-parado" parado position={PONTO_DE_ESPERA} destination={view.destination} altura={altura} folga={folgaParaCartao ? FOLGA_PARADO_COM_CARTAO : FOLGA_PARADO} logosDestino={logos} semTelaCheia={semTelaCheia} tipoDestino={tipoDestino}>
              <div className="mapa-espera-aviso">
                <Clock size={14} />
                Aguardando o motorista sair
              </div>
            </MapaAoVivo>
          )}
        </Suspense>
      </SeOMapaFalhar>
      {comLinha && <LinhaDoMapa view={view} rota={rota} agora={agora} comMapa={comMapa} falhando={falhando} />}
    </div>
  )
}

// Embaixo do mapa: quanto falta e se a posição é recente. O status, as
// etapas e o histórico já estão no cartão em volta.
function LinhaDoMapa({ view, rota, agora, comMapa, falhando }) {
  const linha = 'flex flex-wrap items-center gap-x-3.5 gap-y-1 border-t border-[#F1E8DF] bg-[#FCFAF7] px-5 py-3 text-sm text-[#6b5556] sm:px-8'
  // Parado: o aviso "Aguardando o motorista sair" já está no mapa.
  if (!comMapa) return null
  const fraco = sinalFraco(view.position.at, agora)
  const falta = distanciaTexto(rota ? rota.metros : distanciaMetros(view.position, view.destination))
  const ponto = <span className="text-[#CDBDB0]" aria-hidden="true">·</span>
  return (
    <div className={linha}>
      <strong className="font-bold text-[#3F2021]">
        {view.status === 'arriving' ? 'No endereço' : rota ? `Chega em ~${Math.max(1, Math.round(rota.segundos / 60))} min` : `${falta} até o destino`}
      </strong>
      {rota && view.status !== 'arriving' && (
        <>
          {ponto}
          <span>{falta}</span>
        </>
      )}
      {ponto}
      <span className={fraco ? 'text-amber-700' : ''}>
        {falhando ? 'Sem conexão, tentando de novo…' : `${fraco ? 'última posição' : 'atualizado'} ${haQuanto(view.position.at, agora)}`}
      </span>
      <span className="basis-full text-xs text-[#8a7778]">{AVISO_GPS}</span>
    </div>
  )
}

